<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Assets</h4>

          <div class="flex md:ml-auto mt-4 md:mt-0">
            <VDropdown v-if="$membershipHasPermissionTo('create assets')" right class="ml-4">
              <template #button="dropdownProps">
                <BaseButton primary @click="dropdownProps.open"> New Asset </BaseButton>
              </template>

              <template #default="dropdownProps">
                <VDropdownMenu>
                  <a
                    role="button"
                    :class="[
                      'block',
                      'px-4',
                      'py-2',
                      'hover:bg-gray-200',
                      'transition-color',
                      'transition-fast',
                      'text-right',
                    ]"
                    @click="dropdownProps.close(() => handleOpenMediaCreateModal())"
                    >Media</a
                  >
                  <a
                    role="button"
                    :class="[
                      'block',
                      'px-4',
                      'py-2',
                      'hover:bg-gray-200',
                      'transition-color',
                      'transition-fast',
                      'text-right',
                    ]"
                    @click="dropdownProps.close(() => handleOpenLinkCreateModal())"
                    >Link</a
                  >
                  <a
                    role="button"
                    :class="[
                      'block',
                      'px-4',
                      'py-2',
                      'hover:bg-gray-200',
                      'transition-color',
                      'transition-fast',
                      'text-right',
                    ]"
                    @click="dropdownProps.close(() => handleOpenNoteCreateModal())"
                    >Note</a
                  >
                </VDropdownMenu>
              </template>
            </VDropdown>
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 py-4 lg:px-12 lg:py-12">
      <div class="max-w-7xl mx-auto space-y-8">
        <div class="space-y-2">
          <h3 class="text-base font-medium text-gray-600">Filters</h3>
          <AccountAssetFilters :account-id="accountId" />
        </div>
        <div class="space-y-2">
          <h3 class="text-base font-medium text-gray-600">Result</h3>
          <template v-if="assets.length">
            <AssetGroup
              :assets="assets"
              :selected-asset-ids.sync="selectedAssetIds"
              @view-note="handleViewNote"
            />

            <div v-if="meta.current_page < meta.last_page" class="text-center">
              <BaseButton
                primary
                :disabled="loading"
                @click="fetchAssets({ page: meta.current_page + 1 })"
              >
                Load more ...
              </BaseButton>
            </div>
          </template>
          <BaseCard v-else-if="!initializing">
            <BaseCardBody>
              <div class="p-12">No assets found.</div>
            </BaseCardBody>
          </BaseCard>
        </div>
      </div>
    </div>

    <Transition
      enter-active-class="transition-all duration-150 ease-out transform"
      leave-active-class="transition-all duration-150 ease-in transform"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="hasSelectedAssets"
        class="absolute bottom-4 left-0 right-0 w-full max-w-lg mx-auto rounded bg-white shadow-lg ring-2 ring-black ring-opacity-5 p-4"
      >
        <div class="flex space-x-2">
          <BaseButton primary class="flex-1" @click="editSelectedAssetTags"> Edit Tags </BaseButton>
          <BaseButton ghost class="flex-1" @click="clearSelectedAssetIds">
            Clear Selection
          </BaseButton>
        </div>
      </div>
    </Transition>

    <portal to="app-modal">
      <ViewNoteModal v-if="noteId" :note-id="noteId" @closed="noteId = false" />
      <AccountLinkAssetCreateModal
        v-else-if="openLinkCreateModal"
        :account-id="accountId"
        @created="fetchAssets"
        @closed="openLinkCreateModal = false"
      />
      <AccountNoteAssetCreateModal
        v-else-if="openNoteCreateModal"
        :account-id="accountId"
        @created="fetchAssets"
        @closed="openNoteCreateModal = false"
      />
      <AccountMediaAssetCreateModal
        v-else-if="openMediaCreateModal"
        :account-id="accountId"
        @created="fetchAssets"
        @closed="openMediaCreateModal = false"
      />
    </portal>
  </div>
</template>

<script>
import AccountAssetFilters from 'Components/AccountAssets/AccountAssetFilters.vue';
import VDropdown from 'Components/Common/VDropdown.vue';
import VDropdownMenu from 'Components/Common/VDropdownMenu.vue';
import AssetGroup from 'Components/Assets/AssetGroup.vue';
import ViewNoteModal from 'Components/Notes/ViewNoteModal.vue';
import AccountLinkAssetCreateModal from 'Components/AccountAssets/AccountLinkAssetCreateModal.vue';
import AccountNoteAssetCreateModal from 'Components/AccountAssets/AccountNoteAssetCreateModal.vue';
import AccountMediaAssetCreateModal from 'Components/AccountAssets/AccountMediaAssetCreateModal.vue';

export default {
  name: 'AssetsIndexView',
  components: {
    AccountAssetFilters,
    AssetGroup,
    ViewNoteModal,
    VDropdown,
    VDropdownMenu,
    AccountLinkAssetCreateModal,
    AccountNoteAssetCreateModal,
    AccountMediaAssetCreateModal,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      initializing: true,
      loading: false,
      noteId: null,
      openMediaCreateModal: false,
      openLinkCreateModal: false,
      openNoteCreateModal: false,
      selectedAssetIds: [],
    };
  },
  computed: {
    assets() {
      return this.$store.getters['ui/accountAssets/itemsByParent'](this.accountId);
    },
    meta() {
      return this.$store.state.ui.accountAssets.meta;
    },
    hasSelectedAssets() {
      return Array.isArray(this.selectedAssetIds) && this.selectedAssetIds.length > 0;
    },
  },
  watch: {
    $route() {
      this.fetchAssets({}, false);
    },
  },
  created() {
    this.fetchAssets({}, false);
  },
  async destroyed() {
    try {
      await this.$store.dispatch({
        type: 'ui/accountAssets/clear',
        accountId: this.accountId,
      });
    } catch (error) {
      //
    }
  },
  methods: {
    clearSelectedAssetIds() {
      this.selectedAssetIds = [];
    },
    editSelectedAssetTags() {
      this.$modal.show({
        component: 'AccountAssetBulkEditTags',
        props: {
          accountId: this.accountId,
          assetIds: this.selectedAssetIds,
        },
        on: {},
        hideEvent: 'closed',
      });
    },
    async fetchAssets(params = {}, append = true) {
      const { query: routeQuery } = this.$route;

      try {
        this.loading = true;

        await this.$store.dispatch({
          type: 'ui/accountAssets/load',
          accountId: this.accountId,
          params: {
            ...params,
            ...JSON.parse(JSON.stringify(routeQuery)),
            include: 'assetable,tags',
          },
          append,
        });
      } catch (error) {
        //
      } finally {
        this.loading = false;
        this.initializing = false;
      }
    },
    handleViewNote(note) {
      this.noteId = note.id;
    },
    handleOpenMediaCreateModal() {
      this.openMediaCreateModal = true;
    },
    handleOpenLinkCreateModal() {
      this.openLinkCreateModal = true;
    },
    handleOpenNoteCreateModal() {
      this.openNoteCreateModal = true;
    },
  },
};
</script>
